import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

export default function Guards() {
  const [guards, setGuards] = useState([]);
  const [formData, setFormData] = useState({ username: '', password: '', name: '', role: 'guard' });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadGuards();
  }, []);

  const loadGuards = async () => {
    try {
      const response = await api.getUsers();
      setGuards(response.data);
    } catch (error) {
      console.error('Error loading guards:', error);
      toast.error('Error al cargar usuarios');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createUser(formData);
      toast.success('Usuario creado');
      setFormData({ username: '', password: '', name: '', role: 'guard' });
      setOpen(false);
      loadGuards();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;

    try {
      await api.deleteUser(username);
      toast.success('Usuario eliminado');
      loadGuards();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar usuario');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl" data-testid="guards-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Vigilantes y Usuarios</h1>
            <p className="text-gray-600 mt-1">Gestione los usuarios del sistema</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-guard-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Agregar
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold uppercase tracking-tight">Nuevo Usuario</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="guard_name" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre Completo</Label>
                  <Input
                    id="guard_name"
                    data-testid="guard-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Carlos García"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="guard_username" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Usuario</Label>
                  <Input
                    id="guard_username"
                    data-testid="guard-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Ej: cgarcia"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="guard_password" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Contraseña</Label>
                  <Input
                    id="guard_password"
                    data-testid="guard-password-input"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    className="h-11"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="guard_role" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Rol</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger id="guard_role" data-testid="guard-role-select" className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="guard">Vigilante</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-guard-button" disabled={loading} className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
                    {loading ? 'Guardando...' : 'Guardar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {guards.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No hay usuarios registrados</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {guards.map((guard) => (
                  <tr key={guard.username} className="hover:bg-gray-50 transition-colors" data-testid="guard-row">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{guard.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{guard.username}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${
                        guard.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {guard.role === 'admin' ? 'Admin' : 'Vigilante'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="delete-guard-button"
                        onClick={() => handleDelete(guard.username)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
